import React, { useEffect } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';

export type ConfirmationModalType = 'alert' | 'confirm' | 'danger' | 'success';

export interface ConfirmationModalProps {
  isOpen: boolean;
  type?: ConfirmationModalType;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

export default function ConfirmationModal({
  isOpen,
  type = 'confirm',
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isAlert = type === 'alert' || type === 'success';

  const icons = {
    alert: <Info className="w-6 h-6 text-blue-600" />,
    confirm: <AlertCircle className="w-6 h-6 text-blue-600" />,
    danger: <AlertTriangle className="w-6 h-6 text-rose-600" />,
    success: <CheckCircle className="w-6 h-6 text-emerald-600" />,
  };

  const bgColors = {
    alert: 'bg-blue-100',
    confirm: 'bg-blue-100',
    danger: 'bg-rose-100',
    success: 'bg-emerald-100',
  };

  const confirmBtnStyles = {
    alert: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
    confirm: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
    danger: 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
        onClick={onCancel || onConfirm}
      />

      {/* Modal */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden relative z-10 animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6">
          <div className="flex gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-full ${bgColors[type]} flex items-center justify-center`}>
              {icons[type]}
            </div>
            <div className="pt-1 w-full">
              <h3 className="text-lg font-bold text-slate-900 leading-none mb-2">
                {title}
              </h3>
              <div className="text-sm text-slate-600 font-medium">
                {message}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
          {!isAlert && onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-bold rounded-xl transition-colors ${confirmBtnStyles[type]}`}
          >
            {isAlert ? 'OK' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
